/**
 * Admin Dashboard - Main Admin Page (Refactored)
 * All tabs are now modular components
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Cat, Image, ShoppingBag, Calendar, Heart, Star, Send, Package, 
  Users, BookOpen, Mail, BarChart3, AlertTriangle, CalendarDays, Share2, Camera, Flame, TrendingUp,
  Shield, UserCog, Handshake
} from "lucide-react";
import {
  AdminProvider,
  useAdmin,
  AdminOverviewTab,
  AdminCatsTab,
  AdminAdoptionsTab,
  AdminGalleryTab,
  AdminShopTab,
  AdminBookingsTab,
  AdminCalendarTab,
  AdminDonationsTab,
  AdminReviewsTab,
  AdminMessagesTab,
  AdminOrdersTab,
  AdminBlogTab,
  AdminVolunteersTab,
  AdminNewsletterTab,
  AdminAnalyticsTab,
  AdminLostFoundTab
} from "./admin";
import AdminSocialPostsTab from "./admin/AdminSocialPostsTab";
import AdminHashtagGalleryTab from "./admin/AdminHashtagGalleryTab";
import AdminMemorialsTab from "./admin/AdminMemorialsTab";
import AdminNewsletterAnalyticsTab from "./admin/AdminNewsletterAnalyticsTab";
import AdminAuditLogTab from "./admin/AdminAuditLogTab";
import AdminUsersTab from "./admin/AdminUsersTab";
import AdminPartnershipsTab from "./admin/AdminPartnershipsTab";

const AdminDashboardContent = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { loading } = useAdmin();

  useEffect(() => {
    if (!authLoading && (!user || !user.is_admin)) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-36 flex items-center justify-center bg-gradient-to-br from-amber-50 via-stone-50 to-amber-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-stone-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen pt-28 md:pt-36 bg-gradient-to-br from-amber-50 via-stone-50 to-amber-50" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-stone-900">Admin Dashboard</h1>
          <p className="text-stone-600">Manage CatPals+ website content and operations</p>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <div className="overflow-x-auto -mx-4 px-4">
            <TabsList className="inline-flex min-w-max bg-white/80 backdrop-blur p-1 rounded-xl shadow-sm">
              <TabsTrigger value="overview" className="data-[state=active]:bg-amber-100 rounded-lg">Overview</TabsTrigger>
              <TabsTrigger value="cats" className="data-[state=active]:bg-amber-100 rounded-lg"><Cat className="w-4 h-4 mr-1" />Cats</TabsTrigger>
              <TabsTrigger value="adoptions" className="data-[state=active]:bg-amber-100 rounded-lg"><Users className="w-4 h-4 mr-1" />Adoptions</TabsTrigger>
              <TabsTrigger value="gallery" className="data-[state=active]:bg-amber-100 rounded-lg"><Image className="w-4 h-4 mr-1" />Gallery</TabsTrigger>
              <TabsTrigger value="shop" className="data-[state=active]:bg-amber-100 rounded-lg"><ShoppingBag className="w-4 h-4 mr-1" />Shop</TabsTrigger>
              <TabsTrigger value="bookings" className="data-[state=active]:bg-amber-100 rounded-lg"><Calendar className="w-4 h-4 mr-1" />Bookings</TabsTrigger>
              <TabsTrigger value="calendar" className="data-[state=active]:bg-amber-100 rounded-lg"><CalendarDays className="w-4 h-4 mr-1" />Calendar</TabsTrigger>
              <TabsTrigger value="donations" className="data-[state=active]:bg-amber-100 rounded-lg"><Heart className="w-4 h-4 mr-1" />Donations</TabsTrigger>
              <TabsTrigger value="reviews" className="data-[state=active]:bg-amber-100 rounded-lg"><Star className="w-4 h-4 mr-1" />Reviews</TabsTrigger>
              <TabsTrigger value="messages" className="data-[state=active]:bg-amber-100 rounded-lg"><Send className="w-4 h-4 mr-1" />Messages</TabsTrigger>
              <TabsTrigger value="orders" className="data-[state=active]:bg-amber-100 rounded-lg"><Package className="w-4 h-4 mr-1" />Orders</TabsTrigger>
              <TabsTrigger value="blog" className="data-[state=active]:bg-amber-100 rounded-lg"><BookOpen className="w-4 h-4 mr-1" />Blog</TabsTrigger>
              <TabsTrigger value="volunteers" className="data-[state=active]:bg-amber-100 rounded-lg"><Users className="w-4 h-4 mr-1" />Volunteers</TabsTrigger>
              <TabsTrigger value="newsletter" className="data-[state=active]:bg-amber-100 rounded-lg"><Mail className="w-4 h-4 mr-1" />Newsletter</TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-amber-100 rounded-lg"><BarChart3 className="w-4 h-4 mr-1" />Analytics</TabsTrigger>
              <TabsTrigger value="lost-found" className="data-[state=active]:bg-amber-100 rounded-lg"><AlertTriangle className="w-4 h-4 mr-1" />Lost & Found</TabsTrigger>
              <TabsTrigger value="social-posts" className="data-[state=active]:bg-amber-100 rounded-lg"><Share2 className="w-4 h-4 mr-1" />Social Posts</TabsTrigger>
              <TabsTrigger value="community-gallery" className="data-[state=active]:bg-amber-100 rounded-lg"><Camera className="w-4 h-4 mr-1" />Community</TabsTrigger>
              <TabsTrigger value="memorials" className="data-[state=active]:bg-amber-100 rounded-lg"><Flame className="w-4 h-4 mr-1" />Memorials</TabsTrigger>
              <TabsTrigger value="email-analytics" className="data-[state=active]:bg-amber-100 rounded-lg"><TrendingUp className="w-4 h-4 mr-1" />Email Stats</TabsTrigger>
              <TabsTrigger value="users" className="data-[state=active]:bg-amber-100 rounded-lg"><UserCog className="w-4 h-4 mr-1" />Users</TabsTrigger>
              <TabsTrigger value="partnerships" className="data-[state=active]:bg-amber-100 rounded-lg"><Handshake className="w-4 h-4 mr-1" />Partners</TabsTrigger>
              <TabsTrigger value="audit-log" className="data-[state=active]:bg-amber-100 rounded-lg"><Shield className="w-4 h-4 mr-1" />Audit Log</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview"><AdminOverviewTab /></TabsContent>
          <TabsContent value="cats"><AdminCatsTab /></TabsContent>
          <TabsContent value="adoptions"><AdminAdoptionsTab /></TabsContent>
          <TabsContent value="gallery"><AdminGalleryTab /></TabsContent>
          <TabsContent value="shop"><AdminShopTab /></TabsContent>
          <TabsContent value="bookings"><AdminBookingsTab /></TabsContent>
          <TabsContent value="calendar"><AdminCalendarTab /></TabsContent>
          <TabsContent value="donations"><AdminDonationsTab /></TabsContent>
          <TabsContent value="reviews"><AdminReviewsTab /></TabsContent>
          <TabsContent value="messages"><AdminMessagesTab /></TabsContent>
          <TabsContent value="orders"><AdminOrdersTab /></TabsContent>
          <TabsContent value="blog"><AdminBlogTab /></TabsContent>
          <TabsContent value="volunteers"><AdminVolunteersTab /></TabsContent>
          <TabsContent value="newsletter"><AdminNewsletterTab /></TabsContent>
          <TabsContent value="analytics"><AdminAnalyticsTab /></TabsContent>
          <TabsContent value="lost-found"><AdminLostFoundTab /></TabsContent>
          <TabsContent value="social-posts"><AdminSocialPostsTab /></TabsContent>
          <TabsContent value="community-gallery"><AdminHashtagGalleryTab /></TabsContent>
          <TabsContent value="memorials"><AdminMemorialsTab /></TabsContent>
          <TabsContent value="email-analytics"><AdminNewsletterAnalyticsTab /></TabsContent>
          <TabsContent value="users"><AdminUsersTab /></TabsContent>
          <TabsContent value="partnerships"><AdminPartnershipsTab /></TabsContent>
          <TabsContent value="audit-log"><AdminAuditLogTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const AdminPageRefactored = () => (
  <AdminProvider>
    <AdminDashboardContent />
  </AdminProvider>
);

export default AdminPageRefactored;
